import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const DailyMedia = () => {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await fetch('/admin/api/daily-media.php');
      const data = await response.json();
      setMedia(data);
    } catch (error) {
      console.error('Error fetching daily media:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMedia = media.filter(item => {
    const typeMatch = selectedType === 'all' || item.media_type === selectedType;
    const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory;
    return typeMatch && categoryMatch;
  });

  const uniqueTypes = [...new Set(media.map(item => item.media_type))];
  const uniqueCategories = [...new Set(media.map(item => item.category))];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Daily Media</h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Explore our daily collection of photos and videos showcasing school life, 
              events, activities, and memorable moments from our vibrant community.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Media Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="all">All Categories</option>
                {uniqueCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {filteredMedia.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="fas fa-camera"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Media Found</h3>
              <p className="text-gray-600">No media matches your current filters.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredMedia.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                onClick={() => navigate(`/daily-media/${item.id}`)}
              >
                <div className="relative">
                  {item.media_type === 'image' ? (
                    <img
                      src={`admin/${item.file_path}`}
                      alt={item.title}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-900 flex items-center justify-center relative">
                      <video
                        src={`admin/${item.file_path}`}
                        className="w-full h-full object-cover"
                        controls
                      />
                      <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                        <i className="fas fa-play mr-1"></i>Video
                      </div>
                    </div>
                  )}
                  
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.media_type === 'image' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.media_type}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {item.title}
                  </h3>

                  <div className="space-y-1 mb-3">
                    <div className="flex items-center text-gray-600 text-sm">
                      <i className="fas fa-tag w-3 h-3 mr-2"></i>
                      <span>{item.category}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600 text-sm">
                      <i className="fas fa-calendar w-3 h-3 mr-2"></i>
                      <span>{formatDate(item.date)}</span>
                    </div>
                    
                    {item.location && (
                      <div className="flex items-center text-gray-600 text-sm">
                        <i className="fas fa-map-marker-alt w-3 h-3 mr-2"></i>
                        <span>{item.location}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Media Statistics */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-chart-bar text-teal-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-teal-900 mb-2">
                Media Gallery Statistics
              </h3>
              <p className="text-teal-800 mb-4">
                Our daily media collection captures the essence of school life and creates 
                lasting memories for our students, teachers, and community.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-600">
                    {media.filter(item => item.media_type === 'image').length}
                  </div>
                  <div className="text-sm text-teal-700">Photos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-600">
                    {media.filter(item => item.media_type === 'video').length}
                  </div>
                  <div className="text-sm text-teal-700">Videos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-600">
                    {uniqueCategories.length}
                  </div>
                  <div className="text-sm text-teal-700">Categories</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-600">
                    {media.length}
                  </div>
                  <div className="text-sm text-teal-700">Total Media</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DailyMedia;
